import { initializeApp, getApps } from 'firebase/app';
import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from 'firebase/messaging';

const FCM_TOKEN_KEY = 'utkarsh_fcm_web_token';
const CONFIG_URL = '/firebase-config.json';
const SW_URL = '/firebase-messaging-sw.js?v=5';

let messagingInstance = null;
let cachedConfig = null;
let initPromise = null;
let foregroundUnsub = null;

function isPlaceholder(value) {
  const v = String(value || '').trim();
  return !v || v.startsWith('YOUR_');
}

export function getStoredFcmToken() {
  return (localStorage.getItem(FCM_TOKEN_KEY) || '').trim();
}

export function clearStoredFcmToken() {
  localStorage.removeItem(FCM_TOKEN_KEY);
}

async function loadFirebaseConfig() {
  if (cachedConfig) return cachedConfig;
  const res = await fetch(CONFIG_URL, { cache: 'no-store' });
  if (!res.ok) {
    throw new Error(
      'Missing public/firebase-config.json — copy from firebase-config.example.json'
    );
  }
  const config = await res.json();
  if (
    isPlaceholder(config.apiKey) ||
    isPlaceholder(config.messagingSenderId) ||
    isPlaceholder(config.appId) ||
    isPlaceholder(config.vapidKey)
  ) {
    throw new Error(
      'Fill firebase-config.json with your Firebase Web app + VAPID key values'
    );
  }
  cachedConfig = config;
  return config;
}

export async function isFirebaseMessagingConfigured() {
  try {
    await loadFirebaseConfig();
    return Boolean(await isSupported());
  } catch {
    return false;
  }
}

async function ensureMessaging() {
  if (messagingInstance) return messagingInstance;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    const supported = await isSupported();
    if (!supported) {
      throw new Error('Browser does not support Firebase Messaging');
    }
    const config = await loadFirebaseConfig();
    const app =
      getApps().length > 0
        ? getApps()[0]
        : initializeApp({
            apiKey: config.apiKey,
            authDomain: config.authDomain,
            projectId: config.projectId,
            storageBucket: config.storageBucket,
            messagingSenderId: config.messagingSenderId,
            appId: config.appId,
          });
    messagingInstance = getMessaging(app);
    return messagingInstance;
  })();

  try {
    return await initPromise;
  } catch (err) {
    initPromise = null;
    throw err;
  }
}

async function waitForActiveWorker(registration) {
  if (registration.active) return registration.active;
  const worker = registration.installing || registration.waiting;
  if (!worker) {
    await navigator.serviceWorker.ready;
    return registration.active;
  }
  await new Promise((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error('Service worker activate timeout')),
      15000
    );
    worker.addEventListener('statechange', () => {
      if (worker.state === 'activated') {
        clearTimeout(timer);
        resolve();
      }
    });
  });
  return registration.active;
}

async function initFirebaseInServiceWorker(registration, config) {
  const sw = await waitForActiveWorker(registration);
  if (!sw) throw new Error('No active service worker');

  await new Promise((resolve, reject) => {
    const channel = new MessageChannel();
    const timer = setTimeout(
      () => reject(new Error('Service worker Firebase init timeout')),
      10000
    );
    channel.port1.onmessage = (event) => {
      clearTimeout(timer);
      if (event.data?.ok) resolve();
      else {
        reject(
          new Error(event.data?.error || 'Service worker Firebase init failed')
        );
      }
    };
    sw.postMessage(
      {
        type: 'INIT_FIREBASE',
        config: {
          apiKey: config.apiKey,
          authDomain: config.authDomain,
          projectId: config.projectId,
          storageBucket: config.storageBucket,
          messagingSenderId: config.messagingSenderId,
          appId: config.appId,
        },
      },
      [channel.port2]
    );
  });
}

async function showLocalNotification(title, body, data = {}) {
  if (Notification.permission !== 'granted' || !body) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    await reg.showNotification(title, {
      body,
      icon: '/favicon.svg',
      badge: '/favicon.svg',
      data,
    });
  } catch {
    // eslint-disable-next-line no-new
    new Notification(title, { body, icon: '/favicon.svg' });
  }
}

/**
 * Request permission, init SW + Firebase, get FCM token, start foreground listener.
 */
export async function enableWebPush() {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    throw new Error('Notifications are not available in this browser');
  }

  let permission = Notification.permission;
  if (permission === 'default') {
    permission = await Notification.requestPermission();
  }
  if (permission !== 'granted') {
    throw new Error('Notification permission was blocked');
  }

  const config = await loadFirebaseConfig();
  const messaging = await ensureMessaging();

  const registration = await navigator.serviceWorker.register(SW_URL);
  await initFirebaseInServiceWorker(registration, config);

  const token = await getToken(messaging, {
    vapidKey: config.vapidKey,
    serviceWorkerRegistration: registration,
  });

  if (!token) {
    throw new Error('Could not get an FCM token from Firebase');
  }

  localStorage.setItem(FCM_TOKEN_KEY, token);
  await listenForForegroundMessages();
  return token;
}

/** Foreground FCM — show OS notification via the service worker. */
export async function listenForForegroundMessages(onPayload) {
  try {
    const messaging = await ensureMessaging();
    if (typeof foregroundUnsub === 'function') {
      foregroundUnsub();
      foregroundUnsub = null;
    }
    foregroundUnsub = onMessage(messaging, async (payload) => {
      if (typeof onPayload === 'function') onPayload(payload);
      const title =
        payload?.notification?.title ||
        payload?.data?.title ||
        'Utkarsh';
      const body =
        payload?.notification?.body ||
        payload?.data?.body ||
        '';
      await showLocalNotification(title, body, payload?.data || {});
    });
    return foregroundUnsub;
  } catch {
    return () => {};
  }
}

export function getNotificationPermission() {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
}
